<script>
    import { Select, SelectItem, SelectItemGroup, Button } from "carbon-components-svelte";
    import { createEventDispatcher, onMount } from 'svelte';
    import { fetchJobs, fetchJobsLive } from "../../data";
    import { extractAvailableDates, getCustomerName, getTruckRego, getDriverName } from "./reportHelpers.js";

    const dispatch = createEventDispatcher();

    export let reportMode = 'job'; // 'job' | 'date'
    export let activeStatusScope = 'all'; // 'all' | 'active' | 'completed'
    export let selectedJobId = 'all';
    export let selectedDate = '';
    export let selectedTruckId = 'all';
    export let activeViewMode = 'summary'; // 'summary' | 'detail'

    let allJobs = [];
    let loading = true;
    let error = null;
    let availableDates = [];

    onMount(async () => {
        await loadJobsData();
    });

    async function loadJobsData() {
        loading = true;
        error = null;
        try {
            const [liveRes, staticRes] = await Promise.all([
                fetchJobsLive().catch(() => null),
                fetchJobs(['active', 'completed']).catch(() => null)
            ]);

            const liveJobs = (liveRes && Array.isArray(liveRes.jobs)) ? liveRes.jobs : [];
            const staticJobs = (staticRes && Array.isArray(staticRes.jobs)) ? staticRes.jobs : [];

            const mergedMap = new Map();
            staticJobs.forEach(j => mergedMap.set(String(j.id), j));
            liveJobs.forEach(j => mergedMap.set(String(j.id), j));

            allJobs = Array.from(mergedMap.values());
            availableDates = extractAvailableDates(allJobs);

            // Default selected date to most recent date available
            if (!selectedDate && availableDates.length > 0) {
                selectedDate = availableDates[0].date;
            }

            // Default selected job to first job in scope
            const inScope = getFilteredJobs(allJobs, activeStatusScope);
            if (inScope.length > 0 && selectedJobId === 'all') {
                selectedJobId = String(inScope[0].id);
            }

            loading = false;
            emitReport();
        } catch (err) {
            console.error("Failed to load jobs for reports:", err);
            error = err.message || "Failed to load jobs";
            loading = false;
        }
    }

    function getFilteredJobs(jobsList, scope = activeStatusScope) {
        return (jobsList || []).filter(job => {
            if (scope === 'all') return true;
            return String(job.status || '').toLowerCase() === scope.toLowerCase();
        });
    }

    $: filteredJobs = getFilteredJobs(allJobs, activeStatusScope);

    // Get list of trucks available in current scope
    $: availableTrucks = (() => {
        const truckMap = new Map();
        let targetJobs = filteredJobs;
        if (selectedJobId !== 'all') {
            targetJobs = targetJobs.filter(j => String(j.id) === String(selectedJobId) || String(j.job_number) === String(selectedJobId));
        }

        targetJobs.forEach(j => {
            (j.materials || []).forEach(m => {
                (m.allocated_trucks || []).forEach(t => {
                    const rego = getTruckRego(t);
                    const driver = getDriverName(t);
                    if (!truckMap.has(rego)) {
                        truckMap.set(rego, { rego, driver });
                    }
                });
            });
        });
        return Array.from(truckMap.values());
    })();

    function handleStatusScopeChange(scope) {
        activeStatusScope = scope;
        const inScope = getFilteredJobs(allJobs, scope);
        if (reportMode === 'job') {
            selectedJobId = inScope.length > 0 ? String(inScope[0].id) : 'all';
        } else {
            selectedJobId = 'all';
        }
        selectedTruckId = 'all';
        emitReport(scope);
    }

    function handleReportModeChange(mode) {
        reportMode = mode;
        if (mode === 'date') {
            if (!selectedDate && availableDates.length > 0) {
                selectedDate = availableDates[0].date;
            }
            selectedJobId = 'all';
        } else {
            const inScope = getFilteredJobs(allJobs, activeStatusScope);
            selectedJobId = inScope.length > 0 ? String(inScope[0].id) : 'all';
        }
        selectedTruckId = 'all';
        emitReport();
    }

    function handleJobChange(jobId) {
        selectedJobId = jobId;
        selectedTruckId = 'all';
        emitReport();
    }

    function handleDateChange(date) {
        selectedDate = date;
        selectedTruckId = 'all';
        emitReport();
    }

    function handleTruckChange(truckId) {
        selectedTruckId = truckId;
        if (truckId !== 'all') {
            activeViewMode = 'detail';
        }
        emitReport();
    }

    function handleViewModeChange(view) {
        activeViewMode = view;
        emitReport();
    }

    function emitReport(scope = activeStatusScope) {
        const inScope = getFilteredJobs(allJobs, scope);
        dispatch("generateReport", {
            reportMode,
            statusScope: scope,
            selectedJobId,
            selectedDate,
            selectedTruckId,
            activeViewMode,
            jobs: inScope
        });
    }

    function resetFilters() {
        activeStatusScope = 'all';
        reportMode = 'job';
        const inScope = getFilteredJobs(allJobs, 'all');
        selectedJobId = inScope.length > 0 ? String(inScope[0].id) : 'all';
        selectedTruckId = 'all';
        activeViewMode = 'summary';
        if (availableDates.length > 0) selectedDate = availableDates[0].date;
        emitReport('all');
    }
</script>

<div class="selector-card">
    <div class="selector-header">
        <div>
            <h3 class="selector-title">Report Parameters</h3>
            <span class="selector-subtitle">Configure loads & shift filter</span>
        </div>
        <button type="button" class="btn-reset" on:click={resetFilters} title="Reset filters">
            Reset
        </button>
    </div>

    <!-- 1. Job Status Scope: All / Active / Completed -->
    <div class="filter-section">
        <span class="section-label">Job Status</span>
        <div class="segmented-control">
            <button
                type="button"
                class="segment-btn {activeStatusScope === 'all' ? 'active' : ''}"
                on:click={() => handleStatusScopeChange('all')}
            >
                All Jobs
            </button>
            <button
                type="button"
                class="segment-btn {activeStatusScope === 'active' ? 'active' : ''}"
                on:click={() => handleStatusScopeChange('active')}
            >
                Active
            </button>
            <button
                type="button"
                class="segment-btn {activeStatusScope === 'completed' ? 'active' : ''}"
                on:click={() => handleStatusScopeChange('completed')}
            >
                Completed
            </button>
        </div>
    </div>

    <!-- 2. Report Scope: By Entire Job or By Date -->
    <div class="filter-section">
        <span class="section-label">Report Type</span>
        <div class="mode-control">
            <button
                type="button"
                class="mode-btn {reportMode === 'job' ? 'active' : ''}"
                on:click={() => handleReportModeChange('job')}
            >
                <span class="mode-icon">📋</span>
                <span class="mode-text">By Entire Job</span>
            </button>
            <button
                type="button"
                class="mode-btn {reportMode === 'date' ? 'active' : ''}"
                on:click={() => handleReportModeChange('date')}
            >
                <span class="mode-icon">📅</span>
                <span class="mode-text">By Date (Shift)</span>
            </button>
        </div>
    </div>

    <!-- 3. Form Selectors -->
    {#if loading}
        <div class="selector-loading">
            <span class="loading-spinner"></span>
            <span>Loading active & completed jobs...</span>
        </div>
    {:else if error}
        <div class="selector-error">
            <span>{error}</span>
            <button type="button" class="btn-retry" on:click={loadJobsData}>Retry</button>
        </div>
    {:else}
        <div class="form-fields">
            <!-- If By Date: Date Selector -->
            {#if reportMode === 'date'}
                <div class="field-item">
                    <Select
                        light
                        size="sm"
                        labelText="Operation Date"
                        selected={selectedDate}
                        on:change={(e) => handleDateChange(e.target.value)}
                    >
                        {#each availableDates as d}
                            <SelectItem value={d.date} text="{d.label} ({d.loadsCount} loads)" />
                        {/each}
                    </Select>
                </div>
            {/if}

            <!-- Client Job Selector -->
            <div class="field-item">
                <Select
                    light
                    size="sm"
                    labelText={reportMode === 'job' ? "Select Job" : "Job on Date (Optional)"}
                    bind:selected={selectedJobId}
                    on:change={(e) => handleJobChange(e.target.value)}
                >
                    <SelectItem value="all" text={reportMode === 'date' ? "All Jobs on this Date" : `All Jobs in Scope (${filteredJobs.length})`} />
                    {#if activeStatusScope === 'all' || activeStatusScope === 'active'}
                        <SelectItemGroup label="Active Jobs">
                            {#each filteredJobs.filter(j => j.status === 'active') as job}
                                {@const cust = getCustomerName(job)}
                                <SelectItem value={String(job.id)} text="{job.job_number || ('Job #' + job.id)} - {cust}" />
                            {/each}
                        </SelectItemGroup>
                    {/if}
                    {#if activeStatusScope === 'all' || activeStatusScope === 'completed'}
                        <SelectItemGroup label="Completed Jobs">
                            {#each filteredJobs.filter(j => j.status === 'completed') as job}
                                {@const cust = getCustomerName(job)}
                                <SelectItem value={String(job.id)} text="{job.job_number || ('Job #' + job.id)} - {cust}" />
                            {/each}
                        </SelectItemGroup>
                    {/if}
                </Select>
            </div>

            <!-- Vehicle Filter -->
            <div class="field-item">
                <Select
                    light
                    size="sm"
                    labelText="Vehicle Filter"
                    bind:selected={selectedTruckId}
                    on:change={(e) => handleTruckChange(e.target.value)}
                >
                    <SelectItem value="all" text="All Vehicles ({availableTrucks.length})" />
                    {#each availableTrucks as t}
                        <SelectItem value={t.rego} text="{t.rego} - {t.driver}" />
                    {/each}
                </Select>
            </div>

            <!-- View Mode: Summary vs Individual Truck -->
            <div class="field-item">
                <span class="section-label" style="margin-bottom: 4px; display: inline-block;">Report View</span>
                <div class="segmented-control">
                    <button
                        type="button"
                        class="segment-btn {activeViewMode === 'summary' ? 'active' : ''}"
                        on:click={() => handleViewModeChange('summary')}
                    >
                        Summary
                    </button>
                    <button
                        type="button"
                        class="segment-btn {activeViewMode === 'detail' ? 'active' : ''}"
                        on:click={() => handleViewModeChange('detail')}
                    >
                        Individual Truck
                    </button>
                </div>
            </div>
        </div>
    {/if}

    <div class="selector-actions">
        <Button
            size="field"
            kind="primary"
            on:click={emitReport}
        >
            Generate Report
        </Button>
    </div>
</div>

<style>
    .selector-card {
        background-color: #ffffff;
        border: 1px solid var(--tt-slate-200);
        border-radius: var(--tt-radius-lg);
        box-shadow: var(--tt-shadow-sm);
        padding: 1.15rem;
        display: flex;
        flex-direction: column;
        gap: 14px;
    }

    .selector-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        border-bottom: 1px solid var(--tt-slate-200);
        padding-bottom: 10px;
    }

    .selector-title {
        margin: 0;
        font-size: 0.9375rem;
        font-weight: 700;
        color: var(--tt-slate-900);
    }

    .selector-subtitle {
        font-size: 0.75rem;
        color: var(--tt-slate-500);
    }

    .btn-reset {
        background: transparent;
        border: none;
        color: var(--tt-primary);
        font-size: 0.75rem;
        font-weight: 600;
        cursor: pointer;
        padding: 2px 6px;
        border-radius: 4px;
    }

    .btn-reset:hover {
        background-color: var(--tt-primary-light);
    }

    .filter-section {
        display: flex;
        flex-direction: column;
        gap: 6px;
    }

    .section-label {
        font-size: 0.6875rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: var(--tt-slate-500);
    }

    .segmented-control {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        background-color: var(--tt-slate-100);
        padding: 3px;
        border-radius: var(--tt-radius-sm);
        gap: 2px;
        border: 1px solid var(--tt-slate-200);
    }

    .segment-btn {
        background: transparent;
        border: none;
        padding: 5px 8px;
        font-size: 0.75rem;
        font-weight: 600;
        color: var(--tt-slate-600);
        border-radius: var(--tt-radius-sm);
        cursor: pointer;
        transition: all 0.15s ease;
        text-align: center;
    }

    .segment-btn.active {
        background-color: #ffffff;
        color: var(--tt-primary);
        box-shadow: 0 1px 2px rgba(0,0,0,0.08);
        font-weight: 700;
    }

    .mode-control {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 6px;
    }

    .mode-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        background-color: var(--tt-slate-50);
        border: 1px solid var(--tt-slate-300);
        border-radius: var(--tt-radius-sm);
        padding: 7px 8px;
        cursor: pointer;
        transition: all 0.15s ease;
    }

    .mode-btn:hover {
        background-color: #ffffff;
        border-color: var(--tt-slate-400);
    }

    .mode-btn.active {
        background-color: var(--tt-primary-light);
        border-color: var(--tt-primary);
        color: var(--tt-primary);
        box-shadow: 0 1px 2px rgba(37, 99, 235, 0.08);
    }

    .mode-btn.active .mode-text {
        font-weight: 700;
        color: var(--tt-primary);
    }

    .mode-icon {
        font-size: 0.9375rem;
    }

    .mode-text {
        font-size: 0.75rem;
        font-weight: 600;
        color: var(--tt-slate-700);
    }

    .form-fields {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    .field-item {
        width: 100%;
    }

    :global(.field-item .bx--select-input) {
        border-radius: var(--tt-radius-sm) !important;
        border-color: var(--tt-slate-300) !important;
        background-color: #ffffff !important;
        font-size: 0.8125rem !important;
    }

    .selector-loading {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 1rem 0;
        font-size: 13px;
        color: var(--tt-slate-600);
    }

    .loading-spinner {
        width: 16px;
        height: 16px;
        border: 2px solid var(--tt-slate-200);
        border-top-color: var(--tt-primary);
        border-radius: 50%;
        animation: spin 0.6s linear infinite;
    }

    @keyframes spin {
        to { transform: rotate(360deg); }
    }

    .selector-error {
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding: 0.75rem;
        background-color: #fef2f2;
        border: 1px solid #fecaca;
        border-radius: var(--tt-radius-sm);
        color: #991b1b;
        font-size: 0.75rem;
    }

    .btn-retry {
        align-self: flex-start;
        background-color: #ffffff;
        border: 1px solid #fca5a5;
        color: #991b1b;
        padding: 2px 8px;
        border-radius: 4px;
        font-weight: 600;
        cursor: pointer;
    }

    .selector-actions {
        margin-top: 6px;
        width: 100%;
    }

    :global(.selector-actions .bx--btn) {
        width: 100% !important;
        justify-content: center !important;
        border-radius: var(--tt-radius-sm) !important;
        font-weight: 600 !important;
    }
</style>
